import { useCallback, useRef, useState } from "react";
import { useQuery, useQueryClient, useMutation, keepPreviousData } from "@tanstack/react-query";

import api from "@/app/_module/services/api"; // Your existing API service
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { useUserContext } from "@/app/_module/store/contexts/userContext";

import type { LeaderboardAgent, LeaderboardResponse } from "../types/types";

interface CustomDateRange {
  from: Date;
  to: Date;
}

interface UseLeaderboardParams {
  primaryMetric?: string;
  period?: string;
  visibleOnly?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number;
  customDateRange?: CustomDateRange;
  agencyIdOverride?: string;
  // Parent god-mode session flag. When true the next fetch sends
  // ?godmode=true so the backend walks the full descendant tree (bypassing
  // sub-agency consent AND individual user opt-outs). Backend re-verifies
  // the caller is actually authorized; unauthorized requests silently
  // degrade to the standard scope.
  requestGodmode?: boolean;
  // Per-agent "My Agency" scope. When true the fetch sends ?myAgencyOnly=true
  // so the backend restricts the leaderboard to the caller's own agency even
  // if the parent enabled agency-wide for everyone.
  myAgencyOnly?: boolean;
  // Per-owner "My Sub-Agencies" scope. When true the fetch sends
  // ?myDownlineOnly=true so the backend restricts the board to the caller's own
  // agency + its downline subtree (not the full parent agency-wide pool).
  myDownlineOnly?: boolean;
}

interface UseLeaderboardReturn {
  data: LeaderboardResponse | null;
  loading: boolean;
  /** Refetching while previous data is still on screen — for a subtle
   *  in-place indicator rather than a full skeleton. */
  isRefreshing: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  manualRefresh: () => Promise<void>;
  canRefresh: boolean;
  cachedAt: string | undefined;
  updateVisibility: (visible: boolean) => Promise<void>;
}

// Query key factory for leaderboard queries
export const leaderboardQueryKeys = {
  all: ['leaderboard'] as const,
  stats: (params: { primaryMetric: string; period: string; visibleOnly: boolean; agencyId?: string }) =>
    ['leaderboard', 'stats', params] as const,
  visibility: ['leaderboard-visibility'] as const,
};

export const useLeaderboard = ({
  primaryMetric = "totalPremium",
  period = "this-month",
  visibleOnly = true,
  autoRefresh = false,
  refreshInterval = 15000, // 15 seconds for more responsive updates
  customDateRange,
  agencyIdOverride,
  requestGodmode,
  myAgencyOnly,
  myDownlineOnly,
}: UseLeaderboardParams = {}): UseLeaderboardReturn => {
  const { user } = useUserContext(); // Get current user context for agency filtering
  const queryClient = useQueryClient(); // Get query client for cache invalidation

  // Extract stable values for query key
  const currentAgencyId = user?.currentAgency?.agency?.id;
  const currentUserType = user?.user?.userType;
  const isSystemAdmin = currentUserType === 'admin';

  // Build query params - include custom date range if period is 'custom'.
  //
  // Normalize to full-day boundaries HERE, in the agent's own timezone, exactly
  // as the agency dashboard does (AgencyStats.tsx getDateFilterParams). "Aug 8"
  // means Aug 8 where the agent is sitting, not where the server is.
  //
  // This used to send the picker value raw and let the backend call setHours(),
  // which runs in the SERVER's timezone (UTC) — so the same day picked by an ET
  // agent produced a different window on each surface and neither was one day
  // long. Normalizing on the client and having both services trust the incoming
  // ISO keeps the leaderboard and the dashboard on identical boundaries.
  const customStartDate = customDateRange?.from
    ? (() => {
        const d = new Date(customDateRange.from);
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
      })()
    : undefined;
  const customEndDate = customDateRange?.to
    ? (() => {
        const d = new Date(customDateRange.to);
        d.setHours(23, 59, 59, 999);
        return d.toISOString();
      })()
    : undefined;

  // Use override if provided, otherwise fall back to current agency. A dual-role
  // admin who also belongs to an agency (has a currentAgencyId) defaults to their
  // agency's leaderboard, like an owner. Only a PURE system admin (no agency)
  // defaults to the platform-wide view.
  const effectiveAgencyId = agencyIdOverride || currentAgencyId || (isSystemAdmin ? undefined : currentAgencyId);

  const queryParams = {
    primaryMetric,
    period,
    visibleOnly,
    agencyId: effectiveAgencyId,
    // Include in query key so god-mode and non-god-mode results never share a cache entry.
    requestGodmode: requestGodmode === true,
    // Distinct cache entry for the narrowed "My Agency" view.
    myAgencyOnly: myAgencyOnly === true,
    // Distinct cache entry for the "My Sub-Agencies" (downline) view.
    myDownlineOnly: myDownlineOnly === true,
    ...(period === 'custom' && customStartDate && customEndDate ? { customStartDate, customEndDate } : {}),
  };

  // Flag the next fetch as a force-refresh. Consumed + reset inside fetchLeaderboardData.
  // Stored as a ref so toggling it doesn't trigger React Query to re-run queryFn.
  const forceFreshRef = useRef(false);

  // Fetch function
  const fetchLeaderboardData = async (): Promise<LeaderboardResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.set('primaryMetric', primaryMetric);
    searchParams.set('period', period);
    searchParams.set('visibleOnly', visibleOnly.toString());
    if (effectiveAgencyId) {
      searchParams.set('agencyId', effectiveAgencyId);
    }
    if (period === 'custom' && customStartDate && customEndDate) {
      searchParams.set('startDate', customStartDate);
      searchParams.set('endDate', customEndDate);
    }
    // Manual refresh button sets this — backend bypasses its 60s cache. Backend
    // rate-limits to 1 per 10s per user so spam clicks don't actually re-fetch.
    if (forceFreshRef.current) {
      searchParams.set('fresh', '1');
      forceFreshRef.current = false;
    }
    if (requestGodmode === true) {
      searchParams.set('godmode', 'true');
    }
    if (myAgencyOnly === true) {
      searchParams.set('myAgencyOnly', 'true');
    }
    if (myDownlineOnly === true) {
      searchParams.set('myDownlineOnly', 'true');
    }

    const url = `${getApiUrl(endpoint.leaderboard.getLeaderboardStats)}?${searchParams.toString()}`;

    const result = await handleApiCall<LeaderboardResponse>(async () => {
      const response = await api.get<LeaderboardResponse>({ url });
      return response;
    });

    // Handle different response structures
    if (result.leaderboard) {
      return result;
    } else if (Array.isArray(result)) {
      return {
        leaderboard: result,
        totalAgents: result.length,
        period: {
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString()
        }
      } as unknown as LeaderboardResponse;
    }
    return result;
  };

  // Don't fetch if period is 'custom' but dates aren't set yet
  const isCustomWithoutDates = period === 'custom' && (!customStartDate || !customEndDate);

  // Use React Query for data fetching with auto-refresh
  const {
    data,
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: leaderboardQueryKeys.stats(queryParams),
    queryFn: fetchLeaderboardData,
    // Changing metric/period/agency changes the query key, which by default
    // drops `data` to undefined and blanks the whole page until the new
    // response lands — it reads as a full reload. Keeping the previous result
    // on screen makes the switch a swap rather than a flash.
    placeholderData: keepPreviousData,
    enabled: !!user?.user?.id && !isCustomWithoutDates, // Don't fetch custom range without dates
    refetchInterval: autoRefresh && !isCustomWithoutDates ? refreshInterval : false,
    refetchOnWindowFocus: true, // Refetch when tab becomes active
    staleTime: 5000, // Consider data stale after 5 seconds
    gcTime: 60000, // Keep in cache for 1 minute
    retry: 2, // Retry failed requests twice
    refetchIntervalInBackground: false, // Don't refetch in background
  });

  // Visibility update mutation
  const visibilityMutation = useMutation({
    mutationFn: async (visible: boolean) => {
      return await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leaderboard.toggleVisibility(visible)),
        });
        return response;
      });
    },
    onSuccess: () => {
      // Invalidate all leaderboard queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: leaderboardQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: leaderboardQueryKeys.visibility });
    },
  });

  const updateVisibility = useCallback(async (visible: boolean) => {
    await visibilityMutation.mutateAsync(visible);
  }, [visibilityMutation]);

  const handleRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Manual refresh: bypass frontend + backend cache once. Spam-click protection —
  // disabled for 10s after click to match backend's per-user rate limit. UI reads
  // `canRefresh` to disable the button; actual server-side enforcement is the
  // controller's cooldown map (frontend can't be trusted to self-limit).
  const [manualRefreshCooldown, setManualRefreshCooldown] = useState(false);
  const manualRefresh = useCallback(async () => {
    if (manualRefreshCooldown) return;
    setManualRefreshCooldown(true);
    forceFreshRef.current = true;
    try {
      await refetch();
    } finally {
      setTimeout(() => setManualRefreshCooldown(false), 10_000);
    }
  }, [refetch, manualRefreshCooldown]);

  // Parse error message
  let errorMessage: string | null = null;
  if (queryError) {
    if (typeof queryError === 'object' && queryError !== null) {
      const errorObj = queryError as {
        message?: string;
        error?: string;
        response?: {
          data?: { message?: string };
          status?: number;
          statusText?: string;
        };
      };
      if (errorObj.response?.status) {
        switch (errorObj.response.status) {
          case 401:
            errorMessage = "Authentication required. Please sign in again.";
            break;
          case 403:
            errorMessage = "You don't have permission to view the leaderboard.";
            break;
          case 404:
            errorMessage = "Leaderboard data not found.";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage = errorObj.response.data?.message || errorObj.response.statusText || "Network error occurred.";
        }
      } else if (errorObj.message) {
        errorMessage = errorObj.message;
      } else if (errorObj.error) {
        errorMessage = errorObj.error;
      } else {
        errorMessage = "Something went wrong while loading the leaderboard";
      }
    } else {
      errorMessage = "Something went wrong while loading the leaderboard";
    }
  }

  return {
    data: data ?? null,
    // `loading` stays true only when there is genuinely nothing to show, so the
    // page doesn't swap to a skeleton on every filter change.
    loading: isLoading && !data,
    /** A refresh is in flight but previous data is still displayed. */
    isRefreshing: isFetching && !!data,
    error: errorMessage,
    refetch: handleRefetch,
    manualRefresh,
    canRefresh: !manualRefreshCooldown && !isLoading,
    cachedAt: (data as any)?.cachedAt as string | undefined,
    updateVisibility,
  };
};

// Helper function to map API metrics to display values
export const formatMetricValue = (
  agent: LeaderboardAgent,
  metric: string
): string => {
  switch (metric) {
    case "totalPremium":
    case "total-premium":
      return `$${agent.totalPremium.toLocaleString()}`;
    case "totalPolicies": // Changed from totalSales
    case "total-policies":
      return (agent.totalPolicies ?? agent.totalSales ?? 0).toString();
    case "appointments":
      return agent.appointments.toString();
    case "callsMade":
    case "calls-made":
      return agent.callsMade.toString();
    default:
      return "0";
  }
};

// Helper function to get metric display name
export const getMetricDisplayName = (metric: string): string => {
  const metricNames: Record<string, string> = {
    "total-policies": "Total Policies", // Changed from Total Sales
    "total-premium": "Total Premium",
    appointments: "Appointments",
    "calls-made": "Calls Made",
  };

  return metricNames[metric] || metric;
};
